import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { onWs } from "../api/ws.js";
import { toast } from "../ui/Toast.js";
import { Panel } from "../ui/Panel.js";
import { Timer } from "../ui/Timer.js";
import { passesApi } from "../store/api.js";
import { BattlePassTrack } from "../store/PassesView.js";
import { PurchaseFx, PurchaseSheet } from "../store/cards.js";
import type { PassView } from "../store/types.js";
import { EVENTS_TABS, useEvents, type EventsTab } from "./state.js";
import { PlazaView } from "./PlazaView.js";
import { DailyView } from "./DailyView.js";
import { ProgressView, type ProgressSegment } from "./ProgressView.js";
import { RewardsStrip } from "./RewardsStrip.js";
import { CalendarView } from "./CalendarView.js";
import { eventRunIdFromRoute } from "./presentation.js";
import "./events.css";

/** Subscribes the live-ops store to pushes for the whole session (badges, toasts, refreshes). */
export function useEventPushes(): void {
  const t = useT();
  useEffect(() => {
    if (!useEvents.getState().runs) void useEvents.getState().refreshRuns();
    if (!useEvents.getState().daily) void useEvents.getState().refreshDaily();
    if (!useEvents.getState().claimable) void useEvents.getState().refreshClaimable();
    const onEvents = location.pathname.startsWith("/app/events");
    let nextNoticeAt = 0;
    const notice = (text: string, reward = false) => {
      if (!location.pathname.startsWith("/app/events") || document.visibilityState !== "visible" || performance.now() < nextNoticeAt) return;
      nextNoticeAt = performance.now() + 15_000;
      if (reward) toast.reward(text); else toast.info(text);
    };
    const offStart = onWs("event.start", () => {
      notice(t("events.push.start"));
      void useEvents.getState().refreshRuns();
    });
    const offPhase = onWs("event.phase", () => {
      notice(t("events.push.phase"));
      void useEvents.getState().refreshRuns();
    });
    const offEnd = onWs("event.end", () => {
      notice(t("events.push.end"));
      void useEvents.getState().refreshRuns();
    });
    const offExtend = onWs("event.extended", () => void useEvents.getState().refreshRuns());
    const offDay = onWs("day.new", () => {
      notice(t("events.push.newDay"), true);
      useEvents.getState().bump();
      void useEvents.getState().refreshClaimable();
      void useEvents.getState().refreshDaily();
      void useEvents.getState().refreshRuns();
    });
    const offMilestone = onWs("event.milestone", () => {
      notice(t("events.push.milestone"), true);
      useEvents.getState().bump();
      void useEvents.getState().refreshClaimable();
      if (onEvents || location.pathname.startsWith("/app/events")) void useEvents.getState().refreshRuns();
    });
    const offMission = onWs("mission.done", (e) => {
      const p = e.payload as { points: number };
      notice(t("events.push.mission", { n: p.points }), true);
      useEvents.getState().bump();
      void useEvents.getState().refreshClaimable();
      if (location.pathname.startsWith("/app/events")) void useEvents.getState().refreshDaily();
    });
    const offDrip = onWs("pass.drip", () => notice(t("events.push.drip")));
    const offLogin = onWs("login.day", () => void useEvents.getState().refreshDaily());
    return () => {
      offStart();
      offPhase();
      offEnd();
      offExtend();
      offDay();
      offMilestone();
      offMission();
      offDrip();
      offLogin();
    };
  }, [t]);
}

function PassTab() {
  const t = useT();
  const [pass, setPass] = useState<PassView | null>(null);
  const reload = async () => setPass((await passesApi.list()).find((p) => p.kind === "battle") ?? null);
  useEffect(() => {
    void reload();
  }, []);
  if (!pass) return <p className="hint">{t("common.loading")}</p>;
  return (
    <Panel title={pick(pass.name)} actions={pass.expiresAt ? <span className="mono dim"><FunctionalIcon name="clock" /> <Timer finishesAt={pass.expiresAt} /></span> : null}>
      <p className="hint">{t("events.pass.hint")}</p>
      <BattlePassTrack pass={pass} onChange={() => void reload()} />
    </Panel>
  );
}

/** `/app/events[/tab]`: Event Plaza · Daily · Growth · Achievements · Battle Pass · Calendar (70-events.md). */
export function EventsHub({ route }: { route: string }) {
  const t = useT();
  const page = useRef<HTMLDivElement>(null);
  const seg = route.split("/")[3];
  // Growth and achievements keep their routes as deep links into the Progress tab (D68).
  const progressSegment: ProgressSegment = seg === "achievements" ? "achievements" : "growth";
  const tab: EventsTab = seg === "growth" || seg === "achievements" ? "progress" : seg && EVENTS_TABS.includes(seg as EventsTab) ? (seg as EventsTab) : "plaza";
  useLayoutEffect(() => {
    // A link from the bottom of an agenda must open the new tab at its own heading.
    // Keep ordinary scroll positions while data in the current tab refreshes.
    const scroll = page.current?.closest<HTMLElement>(".shell-route-scroll");
    if (scroll) scroll.scrollTop = 0;
  }, [tab]);
  const refreshRuns = useEvents((s) => s.refreshRuns);
  const seen = useEvents((s) => s.seen);
  const runs = useEvents((s) => s.runs);
  const daily = useEvents((s) => s.daily);
  const claimable = useEvents((s) => s.claimable);
  useEffect(() => {
    void refreshRuns();
    seen();
  }, [refreshRuns, seen]);
  const plazaBadge = runs?.filter((r) => r.category !== "daily").reduce((n, r) => n + r.milestones.filter((m) => m.claimable).length, 0) ?? 0;
  const dailyBadge = (daily?.login.claimable ? 1 : 0) + (daily?.activity.chests.filter((c) => c.claimable).length ?? 0) + (daily?.intel.missions.filter((m) => m.claimable).length ?? 0);
  const progressBadge = claimable?.items.filter((i) => i.kind === "growth" || i.kind === "chapter" || i.kind === "achievement").length ?? 0;
  const badges: Partial<Record<EventsTab, number>> = { plaza: plazaBadge, daily: dailyBadge, progress: progressBadge };
  return (
    <div ref={page} className="page events" data-testid="events-hub">
      <button className="daily-hunt-entry" onClick={() => navigate("/app/nexus")} data-testid="events-nexus"><GameIcon id="zmb.brute" size={72} /><span><strong>NEXUS · HQ 3</strong><small>{pick({ de: "5 Wellen · Truppwechsel · Ausrüstung bergen", en: "5 waves · Switch squads · Recover equipment" })}</small></span></button>
      <button className="daily-hunt-entry" onClick={()=>navigate("/app/operations")} data-testid="events-daily-hunt"><GameIcon id="ui.equipment.weapon" size={56}/><span><strong>{pick({de:"Infiziertenjagd & Ausrüstung",en:"Infected hunt & equipment"})}</strong><small>{pick({de:"Täglich neu um 00:00 (Berlin) · Kisten verdienen, öffnen und ausrüsten",en:"Resets daily at 00:00 Berlin · Earn, open and equip chests"})}</small></span></button>
      <RewardsStrip />
      <nav className="events-navigation" aria-label={t("events.redesign.overview")}>
        {EVENTS_TABS.map((x) => (
          <button key={x} type="button" className={`tab ${x === tab ? "active" : ""}`} aria-current={x === tab ? "page" : undefined} onClick={() => navigate(x === "plaza" ? "/app/events" : x === "progress" ? "/app/events/growth" : `/app/events/${x}`)} data-testid={`events-tab-${x}`}>
            {t(`events.tab.${x}`)}
            {(badges[x] ?? 0) > 0 ? <span className="badge gold" style={{ marginLeft: 6 }}>{badges[x]}</span> : null}
          </button>
        ))}
      </nav>
      {tab === "plaza" ? <PlazaView requestedRunId={eventRunIdFromRoute(route)} /> : null}
      {tab === "daily" ? <DailyView /> : null}
      {tab === "progress" ? <ProgressView initial={progressSegment} /> : null}
      {tab === "pass" ? <PassTab /> : null}
      {tab === "calendar" ? <CalendarView /> : null}
      <PurchaseSheet />
      <PurchaseFx />
    </div>
  );
}
